import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
    Plus, Search, Briefcase, Users, TrendingUp, Loader2, Bot,
    ChevronRight, Building2, MapPin, Clock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
    draft: 'bg-stone-100 text-stone-600',
    for_review: 'bg-blue-100 text-blue-700',
    active: 'bg-emerald-100 text-emerald-700',
    on_hold: 'bg-amber-100 text-amber-700',
    completed: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-600',
};

export default function ATSDashboard() {
    const { getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createForm, setCreateForm] = useState({
        title: '', department: '', employment_type: 'full_time',
        work_location: 'office', slots: 1, account_name: '',
    });
    const [agentActions, setAgentActions] = useState([]);

    const loadJobs = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (search) params.set('search', search);
            if (statusFilter) params.set('status', statusFilter);
            const res = await axios.get(`${API}/ats/jobs?${params}`, { headers: getAuthHeader() });
            setJobs(res.data?.data || []);
        } catch { toast.error('Failed to load jobs'); }
        finally { setLoading(false); }
    }, [getAuthHeader, search, statusFilter]);

    const loadAgentActions = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/ats/agents/actions?status=pending_review&page_size=5`, { headers: getAuthHeader() });
            setAgentActions(res.data?.data || []);
        } catch { /* agent actions optional */ }
    }, [getAuthHeader]);

    useEffect(() => { loadJobs(); loadAgentActions(); }, [loadJobs, loadAgentActions]);

    const handleCreate = async () => {
        if (!createForm.title) return toast.error('Job title is required');
        setCreating(true);
        try {
            const res = await axios.post(`${API}/ats/jobs`, createForm, { headers: getAuthHeader() });
            toast.success('Job order created');
            setShowCreate(false);
            setCreateForm({ title: '', department: '', employment_type: 'full_time', work_location: 'office', slots: 1, account_name: '' });
            navigate(`/recruitment/jobs/${res.data.job_id}`);
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create job'); }
        finally { setCreating(false); }
    };

    // Stats
    const activeJobs = jobs.filter(j => j.status === 'active').length;
    const totalApplicants = jobs.reduce((sum, j) => sum + (j.stats?.total_applicants || 0), 0);
    const pendingActions = agentActions.length;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                        Recruitment
                    </h1>
                    <p className="text-sm text-stone-500 mt-1">Manage job orders, applicants, and AI-powered hiring pipeline</p>
                </div>
                <Button onClick={() => setShowCreate(true)} className="bg-stone-900 text-white hover:bg-stone-800">
                    <Plus className="w-4 h-4 mr-2" /> New Job Order
                </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[
                    { label: 'Active Jobs', value: activeJobs, icon: Briefcase, color: 'text-emerald-600 bg-emerald-50' },
                    { label: 'Total Jobs', value: jobs.length, icon: TrendingUp, color: 'text-blue-600 bg-blue-50' },
                    { label: 'Total Applicants', value: totalApplicants, icon: Users, color: 'text-purple-600 bg-purple-50' },
                    { label: 'Pending AI Actions', value: pendingActions, icon: Bot, color: 'text-amber-600 bg-amber-50' },
                ].map(stat => (
                    <div key={stat.label} className="bg-white rounded-xl border border-stone-200 p-4">
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}>
                                <stat.icon className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-stone-900">{stat.value}</p>
                                <p className="text-xs text-stone-500">{stat.label}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Search and Filters */}
            <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <input
                        value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Search jobs..."
                        className="w-full pl-10 pr-4 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                    />
                </div>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                    className="px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10">
                    <option value="">All Statuses</option>
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="on_hold">On Hold</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                </select>
            </div>

            {/* Agent Actions Feed */}
            {agentActions.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <Bot className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-semibold text-amber-800">Pending AI Actions</span>
                        <span className="ml-auto text-xs text-amber-600 cursor-pointer hover:underline"
                            onClick={() => navigate('/recruitment/agent-queue')}>View all</span>
                    </div>
                    {agentActions.slice(0, 3).map(action => (
                        <div key={action.action_id} className="flex items-center gap-3 py-2 border-t border-amber-200 first:border-0">
                            <span className="text-xs text-amber-700 flex-1">{action.reasoning || action.action_type}</span>
                            <span className="text-xs text-amber-500">{action.agent}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Job Orders Table */}
            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
                </div>
            ) : jobs.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-xl border border-stone-200">
                    <Briefcase className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                    <p className="text-stone-500 text-sm">No job orders yet</p>
                    <Button onClick={() => setShowCreate(true)} variant="outline" className="mt-4">
                        <Plus className="w-4 h-4 mr-2" /> Create your first job order
                    </Button>
                </div>
            ) : (
                <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-stone-100 bg-stone-50/50">
                                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Job Title</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Client</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Status</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Slots</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Applicants</th>
                                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Owner</th>
                                <th className="w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {jobs.map(job => (
                                <tr key={job.job_id}
                                    className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer transition-colors"
                                    onClick={() => navigate(`/recruitment/jobs/${job.job_id}`)}>
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-stone-900 text-sm">{job.title}</div>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            {job.department && <span className="text-xs text-stone-400">{job.department}</span>}
                                            {job.location?.city && (
                                                <span className="text-xs text-stone-400 flex items-center gap-0.5">
                                                    <MapPin className="w-3 h-3" />{job.location.city}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        {job.account_name ? (
                                            <span className="text-sm text-stone-700 flex items-center gap-1">
                                                <Building2 className="w-3.5 h-3.5 text-stone-400" />{job.account_name}
                                            </span>
                                        ) : <span className="text-xs text-stone-400">—</span>}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[job.status] || 'bg-stone-100 text-stone-600'}`}>
                                            {job.status?.replace(/_/g, ' ')}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-stone-700">
                                        {job.filled_slots || 0}/{job.slots || 1}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-stone-700">
                                        {job.stats?.total_applicants || 0}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-stone-500">{job.owner_name || '—'}</td>
                                    <td className="px-4 py-3">
                                        <ChevronRight className="w-4 h-4 text-stone-300" />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Create Job Dialog */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>New Job Order</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Job Title *</label>
                            <input value={createForm.title} onChange={e => setCreateForm({ ...createForm, title: e.target.value })}
                                placeholder="e.g. Customer Service Representative"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Department</label>
                                <input value={createForm.department} onChange={e => setCreateForm({ ...createForm, department: e.target.value })}
                                    placeholder="e.g. Operations"
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Slots</label>
                                <input type="number" min="1" value={createForm.slots}
                                    onChange={e => setCreateForm({ ...createForm, slots: parseInt(e.target.value) || 1 })}
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Type</label>
                                <select value={createForm.employment_type} onChange={e => setCreateForm({ ...createForm, employment_type: e.target.value })}
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10">
                                    <option value="full_time">Full Time</option>
                                    <option value="part_time">Part Time</option>
                                    <option value="contract">Contract</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Location</label>
                                <select value={createForm.work_location} onChange={e => setCreateForm({ ...createForm, work_location: e.target.value })}
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10">
                                    <option value="office">Office</option>
                                    <option value="wfh">Work from Home</option>
                                    <option value="hybrid">Hybrid</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Client Company</label>
                            <input value={createForm.account_name} onChange={e => setCreateForm({ ...createForm, account_name: e.target.value })}
                                placeholder="e.g. Acme Corp (optional)"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                        <Button onClick={handleCreate} disabled={creating} className="bg-stone-900 text-white hover:bg-stone-800">
                            {creating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Creating...</> : 'Create Job Order'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
